import { useSelector } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { selectCareersClientValue } from "../../../../../../App/State/Slices/SelectedCareersClientSlice";
import {
  editFeaturedJobGroupsDisplay,
  getFeaturedJobGroupsDisplay,
} from "./FeaturedJobGroupsDisplayService";
import { basicSort } from "../../../../../../App/Helpers/BasicSort";
import { callAPI } from "../../../../../../App/Helpers/callApi";

/**
 * @description Sets up the Featured Job Group Display State
 */
export function useFeaturedGroupsDisplayState() {
  const selectCareersClientId = useSelector(selectCareersClientValue).id;

  // General component properties
  const [showSignifier, setShowSignifier] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Featured Job Group Display Config properties
  const [carouselDisplayVertically, setCarouselDisplayVertically] =
    useState(false);
  const [groupHasNoCountMessage, setGroupHasNoCountMessage] = useState(false);
  const [groupNoCountMessage, setGroupNoCountMessage] = useState("");
  const [groupAutomaticCarouselAfter, setGroupAutomaticCarouselAfter] =
    useState("");
  const [groupCarouselShowCount, setGroupCarouselShowCount] = useState("");
  const [groupUseCarousel, setGroupUseCarousel] = useState(false);
  const [jobAutomaticCarouselAfter, setJobAutomaticCarouselAfter] =
    useState("");
  const [jobCarouselDisplayVertically, setJobCarouselDisplayVertically] =
    useState(false);
  const [jobCarouselShowCount, setJobCarouselShowCount] = useState("");
  const [jobHasNoCountMessage, setJobHasNoCountMessage] = useState(false);
  const [jobNoCountMessage, setJobNoCountMessage] = useState("");
  const [jobUseCarousel, setJobUseCarousel] = useState(false);
  const [properties, setProperties] = useState([]);

  // Initial get call to Featured Job Groups endpoint
  // Setup intitial local state
  const getFeaturedGroupsDisplayConfig = useCallback(async () => {
    setLoading(true);
    const featuredGroupDisplayConfig = await getFeaturedJobGroupsDisplay(
      selectCareersClientId
    );
    setConfig(featuredGroupDisplayConfig.featuredConfig);
    sortProperties(featuredGroupDisplayConfig);
    setLoading(false);
  }, [selectCareersClientId]);

  // Set local state Job Group Config properties
  const setConfig = (config) => {
    setCarouselDisplayVertically(config.carouselDisplayVertically);
    setGroupHasNoCountMessage(config.groupHasNoCountMessage);
    setGroupNoCountMessage(config.groupNoCountMessage);
    setGroupAutomaticCarouselAfter(config.groupAutomaticCarouselAfter);
    setGroupCarouselShowCount(config.groupCarouselShowCount);
    setGroupUseCarousel(config.groupUseCarousel);
    setJobAutomaticCarouselAfter(config.jobAutomaticCarouselAfter);
    setJobCarouselDisplayVertically(config.jobCarouselDisplayVertically);
    setJobCarouselShowCount(config.jobCarouselShowCount);
    setJobHasNoCountMessage(config.jobHasNoCountMessage);
    setJobNoCountMessage(config.jobNoCountMessage);
    setJobUseCarousel(config.jobUseCarousel);
  };

  // Sort incoming Featured Job Group Config Job Detail properties
  const sortProperties = (config) => {
    config.properties.sort((a, b) => {
      const order = basicSort(
        a.clientFeaturedAdGroupDetailsPropertyConfigurations[0]?.order,
        b.clientFeaturedAdGroupDetailsPropertyConfigurations[0]?.order
      );
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    config.properties.forEach((row) => {
      row.selected =
        row.clientFeaturedAdGroupDetailsPropertyConfigurations?.length > 0;
    });
    setProperties(config.properties);
  };

  // Save Featured Job Group Configuration changes to
  const saveFeaturedJobGroupsDisplay = (isRowDrag) => {
    var addProperties = [];
    var newOrder = 1;
    properties.forEach((row) => {
      if (row.selected) {
        let newProperty = {
          AvailableAdDetailsPropertyId: row.id,
          ClientId: selectCareersClientId,
          DisplayName: row.label,
          Order: newOrder,
        };
        newOrder += 1;
        addProperties.push(newProperty);
      }
    });

    let addConfig = {
      ClientId: selectCareersClientId,
      GroupHasNoCountMessage: groupHasNoCountMessage,
      GroupNoCountMessage: groupNoCountMessage,
      GroupUseCarousel: groupUseCarousel,
      GroupAutomaticCarouselAfter: groupAutomaticCarouselAfter,
      GroupCarouselShowCount: groupCarouselShowCount,
      JobHasNoCountMessage: jobHasNoCountMessage,
      JobNoCountMessage: jobNoCountMessage,
      JobUseCarousel: jobUseCarousel,
      JobAutomaticCarouselAfter: jobAutomaticCarouselAfter,
      JobCarouselShowCount: jobCarouselShowCount,
      JobHasLocationData: false,
      JobCarouselDisplayVertically: jobCarouselDisplayVertically,
      CarouselDisplayVertically: carouselDisplayVertically,
    };

    let data = {
      ClientId: selectCareersClientId,
      selectedProperties: addProperties,
      featuredConfig: addConfig,
    };

    if (isRowDrag === true) {
      editFeaturedJobGroupsDisplay(selectCareersClientId, data);
    } else {
      callAPI(
        editFeaturedJobGroupsDisplay(selectCareersClientId, data),
        "Featured Group Display Saved",
        setShowSignifier,
        setResponseMessage,
        setShowError,
        setProcessing
      );
    }
  };

  useEffect(() => {
    getFeaturedGroupsDisplayConfig();
  }, [getFeaturedGroupsDisplayConfig]);

  useEffect(() => {
    if (properties.length > 0 && !loading) {
      saveFeaturedJobGroupsDisplay(true);
    }
  }, [properties]);

  return {
    effects: {
      getFeaturedJobGroupsDisplay,
    },
    getters: {
      showSignifier,
      responseMessage,
      showError,
      processing,
      loading,
      selectCareersClientId,
      carouselDisplayVertically,
      groupHasNoCountMessage,
      groupNoCountMessage,
      groupAutomaticCarouselAfter,
      groupCarouselShowCount,
      groupUseCarousel,
      jobAutomaticCarouselAfter,
      jobCarouselDisplayVertically,
      jobCarouselShowCount,
      jobHasNoCountMessage,
      jobNoCountMessage,
      jobUseCarousel,
      properties,
    },
    handlers: {
      saveFeaturedJobGroupsDisplay,
    },
    setters: {
      setShowSignifier,
      setShowError,
      setProcessing,
      setLoading,
      setCarouselDisplayVertically,
      setGroupHasNoCountMessage,
      setGroupNoCountMessage,
      setGroupAutomaticCarouselAfter,
      setGroupCarouselShowCount,
      setGroupUseCarousel,
      setJobAutomaticCarouselAfter,
      setJobCarouselDisplayVertically,
      setJobCarouselShowCount,
      setJobHasNoCountMessage,
      setJobNoCountMessage,
      setJobUseCarousel,
      setProperties,
    },
  };
}
